import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type { ILineItemTaxApi } from './api';
import { LineItemTaxApi } from './api';
import type {
  CountriesTaxCodesControllerResultTypes,
  CountryResultDto,
  LineItemConfigurationControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const ApiContext = createContext<ILineItemTaxApi | undefined>(undefined);
const { useApi: useLineItemTaxApi, ApiProvider: LineItemTaxApiProvider } =
  makeApiProvider({
    name: 'LineItemTax',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new LineItemTaxApi(tenantId, auth, apiUrl),
  });

export { LineItemTaxApiProvider };

const GET_TAX_LINE_CONFIGURATIONS = (
  globalServiceProviderId: string | undefined
): QueryKey => ['get-tax-line-configurations', globalServiceProviderId];

const GET_TAX_CODES_BY_COUNTRY = (countryId: string | undefined): QueryKey => [
  'get-tax-codes-by-country',
  ...(countryId ? [countryId] : []),
];

const GET_ALL_COUNTRIES = (): QueryKey => ['get-all-countries'];

export const useGetTaxCodesByCountry = (
  countryId: string | undefined
): UseQueryResult<
  CountriesTaxCodesControllerResultTypes['getTaxCodes'],
  unknown
> => {
  return useApiQuery(
    useLineItemTaxApi,
    GET_TAX_CODES_BY_COUNTRY(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }

      return api.getTaxCodesByCountry({
        params: {
          countryId,
        },
      });
    },
    { enabled: !!countryId }
  );
};

export interface CountryWithTranslatedLabel extends CountryResultDto {
  label: string;
  value: string;
}

export const useGetAllCountries = (
  options?: Omit<
    UseQueryOptions<
      CountriesTaxCodesControllerResultTypes['getCountries'],
      unknown
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<CountryWithTranslatedLabel[], unknown> => {
  const { t } = useTranslation();

  const translateAndSortCountries = useCallback(
    (countries: CountryResultDto[]): CountryWithTranslatedLabel[] => {
      const options = countries?.map((country) => {
        return {
          ...country,
          value: country.id,
          label: t(`country.${country.isoCode}`),
        };
      });

      options?.sort((a, b) => a.label.localeCompare(b.label));
      return options ?? [];
    },
    [t]
  );

  return useApiQuery(
    useLineItemTaxApi,
    GET_ALL_COUNTRIES(),
    (api) => {
      return api.getAllCountries();
    },
    {
      ...options,
      select: translateAndSortCountries,
    }
  );
};

export const useGetTaxLineConfigurations = (
  globalServiceProviderId: string | undefined
): UseQueryResult<
  LineItemConfigurationControllerResultTypes['getLineItemConfigurations'],
  unknown
> => {
  return useApiQuery(
    useLineItemTaxApi,
    GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId),
    (api) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.getLineItemConfigurations({
        params: {
          globalServiceProviderId,
        },
      });
    },
    {
      enabled: !!globalServiceProviderId,
    }
  );
};
